import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { ArrowLeft, FileText, Users, Save, AlertCircle, BookOpen } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function EnterMarks() {
  const navigate = useNavigate();
  const { assignmentId } = useParams(); // This is class_id from routine
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [classInfo, setClassInfo] = useState(null);
  const [students, setStudents] = useState([]);
  const [exams, setExams] = useState([]);
  const [selectedExam, setSelectedExam] = useState(null);
  const [maxMarks, setMaxMarks] = useState(100);
  const [marks, setMarks] = useState({});
  const [existingMarks, setExistingMarks] = useState([]);

  useEffect(() => {
    fetchStudents();
    fetchExams();
  }, [assignmentId]);

  useEffect(() => {
    if (selectedExam && classInfo) {
      fetchExistingMarks();
      // Set max marks from exam
      setMaxMarks(selectedExam.total_marks || 100);
    }
  }, [selectedExam, classInfo]);

  const fetchStudents = async () => {
    try {
      const token = localStorage.getItem('staffToken');
      if (!token) {
        navigate('/staff-portal');
        return;
      }

      const response = await fetch(`${API_URL}/api/staff-portal/my-classes/${assignmentId}/students`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        const data = await response.json();
        setClassInfo(data.class_info);
        setStudents(data.students || []);
        
        // Initialize marks as empty
        const initialMarks = {};
        (data.students || []).forEach(s => {
          initialMarks[s.id] = '';
        });
        setMarks(initialMarks);
      } else if (response.status === 401) {
        localStorage.removeItem('staffToken');
        navigate('/staff-portal');
      } else {
        toast.error('Failed to fetch students');
        navigate('/staff-portal/my-classes');
      }
    } catch (error) {
      console.error('Error:', error);
      toast.error('Failed to fetch students');
    } finally {
      setLoading(false);
    }
  };

  const fetchExams = async () => {
    try {
      const token = localStorage.getItem('staffToken');
      const response = await fetch(
        `${API_URL}/api/staff-portal/my-classes/${assignmentId}/exams`,
        { headers: { 'Authorization': `Bearer ${token}` } }
      );

      if (response.ok) {
        const data = await response.json();
        setExams(data.exams || []);
      }
    } catch (error) {
      console.error('Error fetching exams:', error);
    }
  };

  const fetchExistingMarks = async () => {
    if (!selectedExam) return;
    
    try {
      const token = localStorage.getItem('staffToken');
      const response = await fetch(
        `${API_URL}/api/staff-portal/marks/${assignmentId}?exam_id=${selectedExam.id}`,
        { headers: { 'Authorization': `Bearer ${token}` } }
      );

      if (response.ok) {
        const data = await response.json();
        setExistingMarks(data.marks || []);
        
        // Update marks state with existing records
        const existingMarksMap = {};
        (data.marks || []).forEach(record => {
          existingMarksMap[record.student_id] = record.marks_obtained;
        });
        
        // Merge with initialized marks
        const newMarks = {};
        students.forEach(s => {
          newMarks[s.id] = existingMarksMap[s.id] !== undefined ? existingMarksMap[s.id] : '';
        });
        setMarks(newMarks);
      }
    } catch (error) {
      console.error('Error fetching existing marks:', error);
    }
  };

  const handleExamChange = (examId) => {
    const exam = exams.find(e => e.id === examId);
    setSelectedExam(exam);
    
    // Reset marks when exam changes
    const resetMarks = {};
    students.forEach(s => {
      resetMarks[s.id] = '';
    });
    setMarks(resetMarks);
    setExistingMarks([]);
  };

  const handleMarksChange = (studentId, value) => {
    // Allow empty string or valid number
    if (value === '' || (!isNaN(value) && parseFloat(value) >= 0 && parseFloat(value) <= maxMarks)) {
      setMarks(prev => ({
        ...prev,
        [studentId]: value
      }));
    }
  };

  const handleSave = async () => {
    if (!selectedExam) {
      toast.error('Please select an exam first');
      return;
    }

    const validRecords = Object.entries(marks)
      .filter(([, value]) => value !== '' && !isNaN(value))
      .map(([student_id, marks_obtained]) => ({
        student_id,
        marks_obtained: parseFloat(marks_obtained)
      }));

    if (validRecords.length === 0) {
      toast.error('Please enter marks for at least one student');
      return;
    }

    setSaving(true);
    try {
      const token = localStorage.getItem('staffToken');
      const response = await fetch(`${API_URL}/api/staff-portal/marks/enter`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          class_id: assignmentId,
          exam_id: selectedExam.id,
          max_marks: maxMarks,
          records: validRecords
        })
      });

      if (response.ok) {
        const result = await response.json();
        toast.success(`✅ Marks saved for "${result.exam_name}"! ${result.saved} new, ${result.updated} updated`);
        fetchExistingMarks();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to save marks');
      }
    } catch (error) {
      toast.error('Failed to save marks');
    } finally {
      setSaving(false);
    }
  };

  const calculateGrade = (obtained, max) => {
    if (!obtained || obtained === '') return '-';
    const percentage = (parseFloat(obtained) / max) * 100;
    if (percentage >= 90) return 'A+';
    if (percentage >= 80) return 'A';
    if (percentage >= 70) return 'B+';
    if (percentage >= 60) return 'B';
    if (percentage >= 50) return 'C';
    if (percentage >= 40) return 'D';
    return 'F';
  };

  const getGradeColor = (grade) => {
    switch (grade) {
      case 'A+': case 'A': return 'bg-green-100 text-green-700';
      case 'B+': case 'B': return 'bg-blue-100 text-blue-700';
      case 'C': return 'bg-yellow-100 text-yellow-700';
      case 'D': return 'bg-orange-100 text-orange-700';
      case 'F': return 'bg-red-100 text-red-700';
      default: return 'bg-gray-100 text-gray-500';
    }
  };

  const enteredCount = Object.values(marks).filter(m => m !== '' && !isNaN(m)).length;
  const avgMarks = enteredCount > 0 
    ? (Object.values(marks)
        .filter(m => m !== '' && !isNaN(m))
        .reduce((a, b) => a + parseFloat(b), 0) / enteredCount).toFixed(1)
    : 0;

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50" data-testid="enter-marks-page">
      {/* Header */}
      <header className="bg-blue-700 text-white">
        <div className="max-w-4xl mx-auto px-4 py-4">
          <div className="flex items-center gap-4">
            <Link to="/staff-portal/my-classes" className="p-2 hover:bg-blue-600 rounded-lg">
              <ArrowLeft className="h-5 w-5" />
            </Link>
            <div className="flex-1">
              <h1 className="text-xl font-bold">Enter Marks</h1>
              <p className="text-blue-200 text-sm">
                {classInfo?.course_name} {classInfo?.semester_name && `• ${classInfo.semester_name}`}
                {classInfo?.subject_name && ` • ${classInfo.subject_name}`}
              </p>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-6 space-y-4">
        {/* Exam Selection */}
        <Card>
          <CardContent className="p-4">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  <BookOpen className="inline h-4 w-4 mr-1" />
                  Select Exam *
                </label>
                <select
                  value={selectedExam?.id || ''}
                  onChange={(e) => handleExamChange(e.target.value)}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                  data-testid="exam-select"
                >
                  <option value="">-- Select an Exam --</option>
                  {exams.map(exam => (
                    <option key={exam.id} value={exam.id}>
                      {exam.name} ({exam.exam_type}) - Total: {exam.total_marks || 100}
                    </option>
                  ))}
                </select>
                {exams.length === 0 && (
                  <p className="text-sm text-orange-600 mt-1">
                    ⚠️ No exams found for this subject. Admin needs to create exams first.
                  </p>
                )}
              </div>
              
              <div className="flex items-end gap-4">
                <div className="text-center px-4 py-2 bg-indigo-50 rounded-lg">
                  <p className="text-2xl font-bold text-indigo-600">{enteredCount}/{students.length}</p>
                  <p className="text-xs text-indigo-500">Entered</p>
                </div>
                <div className="text-center px-4 py-2 bg-green-50 rounded-lg">
                  <p className="text-2xl font-bold text-green-600">{avgMarks}</p>
                  <p className="text-xs text-green-500">Avg Marks</p>
                </div>
              </div>
            </div>

            {selectedExam && (
              <div className="mt-4 p-3 bg-blue-50 rounded-lg border border-blue-200">
                <p className="text-sm text-blue-800">
                  <strong>📝 Selected Exam:</strong> {selectedExam.name} | 
                  <strong> Type:</strong> {selectedExam.exam_type} | 
                  <strong> Total Marks:</strong> {selectedExam.total_marks || 100} |
                  <strong> Passing:</strong> {selectedExam.passing_marks || 40}
                </p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Info about existing marks */}
        {selectedExam && existingMarks.length > 0 && (
          <div className="flex items-center gap-2 text-sm text-blue-600 bg-blue-50 p-3 rounded-lg">
            <AlertCircle className="h-4 w-4" />
            <span>{existingMarks.length} students already have marks for this exam. You can update them.</span>
          </div>
        )}

        {/* Students List */}
        {selectedExam ? (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Users className="h-5 w-5" />
                Students ({students.length})
              </CardTitle>
            </CardHeader>
            <CardContent>
              {students.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <Users className="h-12 w-12 mx-auto mb-2 text-gray-300" />
                  <p>No students found in this class</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {students.map((student, index) => {
                    const grade = calculateGrade(marks[student.id], maxMarks);
                    return (
                      <div
                        key={student.id}
                        className="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
                      >
                        <div className="flex items-center gap-3 flex-1">
                          <span className="w-8 h-8 bg-indigo-100 text-indigo-600 rounded-full flex items-center justify-center font-medium text-sm">
                            {index + 1}
                          </span>
                          <div>
                            <p className="font-medium text-gray-900">{student.full_name}</p>
                            <p className="text-xs text-gray-500">
                              {student.student_id || student.roll_number}
                            </p>
                          </div>
                        </div>
                        
                        <div className="flex items-center gap-3">
                          <div className="flex items-center gap-2">
                            <input
                              type="number"
                              value={marks[student.id]}
                              onChange={(e) => handleMarksChange(student.id, e.target.value)}
                              min={0}
                              max={maxMarks}
                              placeholder="--"
                              className="w-20 px-3 py-2 border rounded-lg text-center font-medium"
                              data-testid={`marks-input-${student.id}`}
                            />
                            <span className="text-gray-400">/ {maxMarks}</span>
                          </div>
                          
                          <span className={`px-3 py-1 rounded text-sm font-medium min-w-[50px] text-center ${getGradeColor(grade)}`}>
                            {grade}
                          </span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>
        ) : (
          <Card>
            <CardContent className="p-8 text-center">
              <FileText className="h-16 w-16 mx-auto mb-4 text-gray-300" />
              <h3 className="text-lg font-medium text-gray-700 mb-2">Select an Exam</h3>
              <p className="text-gray-500">
                Please select an exam from the dropdown above to start entering marks.
              </p>
            </CardContent>
          </Card>
        )}

        {/* Save Button */}
        {selectedExam && students.length > 0 && (
          <div className="sticky bottom-4">
            <Button
              onClick={handleSave}
              disabled={saving || !selectedExam}
              className="w-full bg-blue-600 hover:bg-blue-700 py-6 text-lg"
              data-testid="save-marks-btn"
            >
              <Save className="h-5 w-5 mr-2" />
              {saving ? 'Saving...' : `Save Marks for ${selectedExam.name}`}
            </Button>
          </div>
        )}
      </main>
    </div>
  );
}
